import AddCircleOutlineIcon from '@material-ui/icons/AddCircleOutline'
import { differenceInCalendarMonths } from 'date-fns'
import { FormEventHandler, useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useHistory } from 'react-router'
import { FlexBox } from 'shared/components/Base/FlexBox'
import { Box } from 'shared/components/Box/Box'
import { Button } from 'shared/components/Button/Button'
import { SimpleDialog } from 'shared/components/Dialog/SimpleDialog'
import { InlineSpinner } from 'shared/components/Spinner'
import { Text } from 'shared/components/Text/Text'
import { useHelpCenterUrl } from 'shared/hooks/useHelpCenterUrl'
import { primaryBlack } from 'shared/theme/colors'

import { isConsole } from '../../../config/config'
import { Handler } from '../../../helpers/typeHelper'
import { useEmploymentHistory } from '../../../hooks/query/useEmploymentHistory'
import {
  EmploymentJob,
  getEmploymentHistoryMonthsInYears,
  updateEmploymentHistoryItem,
} from '../../../models/employment'
import { generateEmploymentJobsDocument, notifyUserToSignAgreement, User } from '../../../models/user'
import { useUserProvider } from '../../Context/UserContext'
import {
  EMPLOYMENT_HISTORY_MIN_MONTHS_EMPLOYED,
  EMPLOYMENT_HISTORY_MIN_MONTHS_EMPLOYED_YEARS_BACK,
  EMPLOYMENT_ROUTES,
} from './EmploymentHistory.constants'
import { EmploymentHistoryContainer, EmploymentHistoryItemsContainer } from './EmploymentHistory.styles'
import { EmploymentHistoryItem } from './EmploymentHistoryItem/EmploymentHistoryItem'
import { EmploymentHistryItemUnemploymentGapDropdown } from './EmploymentHistoryItem/EmploymentHistryItemUnemploymentGapDropdown'

interface EmploymentHistoryProps {
  user: User
  handleRedirect: Handler
}

export interface EmploymentHistoryForm {
  unemployment: Record<string, string>
}

export function EmploymentHistory({ user, handleRedirect }: EmploymentHistoryProps) {
  const history = useHistory()
  const [showSubmitErrorModal, setShowSubmitErrorModal] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const { employmentHistory, isLoading } = useEmploymentHistory({ userId: user.id })
  const hasIncompleteJobs = employmentHistory?.some((item) => item.isComplete === false)
  const drivingHelpUrl = useHelpCenterUrl('driving-with-cloudtrucks')
  const { showBanner, clearUserCache } = useUserProvider()
  const {
    handleSubmit: _handleSubmit,
    control,
    setValue,
    getFieldState,
  } = useForm<EmploymentHistoryForm>({
    defaultValues: {},
    mode: 'onSubmit',
  })

  useEffect(() => {
    if (hasIncompleteJobs) {
      showBanner('error', { message: 'Please complete any missing information in your employment history.' })
    }
  }, [showBanner, hasIncompleteJobs])

  const closeModal = () => {
    setShowSubmitErrorModal(false)
  }

  const handleSubmitAndShowError: FormEventHandler<HTMLFormElement> = async function (e) {
    await _handleSubmit(handleSubmit)(e)

    if (getFieldState('unemployment')?.invalid) {
      showBanner('error', {
        title: 'Select the reasons for the gaps in your employment history',
        message: 'If you’ve forgotten to add positions, please add them.',
      })
    }
  }

  async function handleSubmit(formValues: EmploymentHistoryForm) {
    setIsSubmitting(true)
    const jobsUnemployment = formValues.unemployment || {}
    const jobGapUpdates = Object.keys(jobsUnemployment).map((jobId) =>
      updateEmploymentHistoryItem(user.id, jobId, {
        details: { unemployment_gap_before_reason: jobsUnemployment[jobId] },
      })
    )

    try {
      await Promise.all(jobGapUpdates)
    } catch (err: any) {
      setIsSubmitting(false)
      return showBanner('error', err)
    }

    if (hasIncompleteJobs) {
      return showBanner('error', {
        message: 'Please complete any missing information in your employment history.',
      })
    }

    const { months_employed } = await getEmploymentHistoryMonthsInYears(user.id, {
      years: EMPLOYMENT_HISTORY_MIN_MONTHS_EMPLOYED_YEARS_BACK,
    })

    if (months_employed >= EMPLOYMENT_HISTORY_MIN_MONTHS_EMPLOYED) {
      try {
        await generateEmploymentJobsDocument(user.id)
        await clearUserCache()
      } catch (err) {
        /* ignore error*/
      }
      if (isConsole()) {
        notifyUserToSignAgreement(user.id)
        handleRedirect()
      } else {
        history.push(EMPLOYMENT_ROUTES.ONBOARDING_EMPLOYMENT_SUBMIT)
      }
    } else {
      setShowSubmitErrorModal(true)
    }

    setIsSubmitting(false)
  }

  function calculateGap(item: EmploymentJob, idx: number) {
    if (!item?.details?.startDate) return null
    if (idx === employmentHistory.length - 1) return null
    const endDate = employmentHistory[idx + 1].details.endDateObject
    if (!endDate) return 0

    return Math.max(differenceInCalendarMonths(item.details.startDateObject, endDate) - 1, 0)
  }

  return (
    <EmploymentHistoryContainer>
      <Text variant="header2" align="center" mb={2}>
        Add your past 10 years of work experience
      </Text>
      <Text variant="paragraph1" align="center" mb={5}>
        CloudTrucks requires <strong>12 months</strong> of verifiable Class 8 Tractor/Trailer driving
        experience.
      </Text>

      <Button
        variant="secondary"
        size="large"
        icon={<AddCircleOutlineIcon color="primary" />}
        href={EMPLOYMENT_ROUTES.ONBOARDING_EMPLOYMENT_HISTORY_ADD}
      >
        Add Position
      </Button>

      <Box mt={6} />
      {isLoading && <InlineSpinner mb={6} />}

      <form onSubmit={handleSubmitAndShowError} style={{ maxWidth: '100%' }}>
        {employmentHistory?.length ? (
          <EmploymentHistoryItemsContainer>
            {employmentHistory.map((item: EmploymentJob, idx: number) => (
              <EmploymentHistoryItem
                key={item.id}
                item={item}
                idx={idx}
                gapInMonths={calculateGap(item, idx)}
                gapChildren={
                  employmentHistory[idx + 1]?.details.isInPast3Years ? (
                    <EmploymentHistryItemUnemploymentGapDropdown
                      control={control}
                      setValue={setValue}
                      job={item}
                    />
                  ) : null
                }
              />
            ))}
          </EmploymentHistoryItemsContainer>
        ) : null}

        <Button variant="primary" type="submit" size="extraLarge" isLoading={isSubmitting} margin="0 auto">
          {isConsole() ? 'Continue' : 'Submit'}
        </Button>
      </form>

      <Box m={2} />
      <Button variant="text" onClick={handleRedirect} size="extraLarge">
        {hasIncompleteJobs ? 'Back to Tasklist' : 'Go back'}
      </Button>

      <SimpleDialog isOpen={showSubmitErrorModal} onClose={closeModal}>
        <Text variant="header2" fontColor={primaryBlack} mb={2} align="left">
          You don’t meet the driver eligibility requirements
        </Text>
        <Text variant="paragraph1" align="left">
          Unfortunately, your work experience doesn’t meet CloudTrucks’ driver eligibility requirements. If
          you forgot to add a position, you can go back to make changes to your employment history.
        </Text>
        <Box m={5} />
        <FlexBox isColumn justifyContent="center" alignItems="center" width="100%">
          <Button variant="primary" onClick={closeModal} margin="0 auto">
            Close
          </Button>
          <Box m={2} />
          <Button margin="0 auto" variant="text" href={drivingHelpUrl} target="_blank">
            Learn more
          </Button>
        </FlexBox>
      </SimpleDialog>
    </EmploymentHistoryContainer>
  )
}
